import { setToken } from './api';
import { currentAccount } from './auth';

export interface Session {
  docName: string | null;
  wsUrl: string;
  user: { name: string; color: string; role: string };
}

function colorFor(name: string): string {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return `hsl(${h} 65% 45%)`;
}

const DEV_USER_KEY = 'niobe.devUser';
const DEV_NAMES = [
  'Alex', 'Robin', 'Sam', 'Mara', 'Leo', 'Nina', 'Théo', 'Iris', 'Hugo', 'Lina',
  'Noah', 'Zoé', 'Eli', 'Jad', 'Maya', 'Otto', 'Rina', 'Cléo', 'Yann', 'Suki',
];

/**
 * Dev-only: a random display name, stable per browser tab (sessionStorage is per-tab),
 * so simply opening several tabs/windows simulates several connected users — distinct
 * cursors AND distinct presence counts (each name → a distinct stub userId). A small
 * numeric suffix keeps two tabs that pick the same base name apart.
 */
function devRandomUser(): string {
  const existing = sessionStorage.getItem(DEV_USER_KEY);
  if (existing) return existing;
  const base = DEV_NAMES[Math.floor(Math.random() * DEV_NAMES.length)] ?? 'Guest';
  const name = `${base} ${Math.floor(Math.random() * 90) + 10}`;
  sessionStorage.setItem(DEV_USER_KEY, name);
  return name;
}

/**
 * Resolve the session from the URL. Dev multi-session: ?user=&role= let each
 * browser tab act as a distinct stub identity (token "Name|Role"). In dev, when no
 * ?user= is given, a random name is generated per tab to simulate multiple users.
 * ?doc= selects the open room. The WS connects to the local Hocuspocus server.
 *
 * The TOKEN is deliberately NOT part of the result. This is resolved once per page (App memoises
 * it with an empty dep array) and the token is the one thing here that expires, so returning it
 * would hand every consumer a value that goes stale in ~60-90 min. Callers read it at the moment
 * of use instead — lib/session-token.ts#freshToken.
 */
export function resolveSession(): Session {
  const params = new URLSearchParams(window.location.search);

  // Entra mode: AuthGate has already signed in and stored the real access token — the identity
  // comes from the validated account, and the ?user=/?role= simulation is deliberately ignored
  // (it must never be able to influence a real session). Dev/stub mode keeps the old behaviour.
  const account = currentAccount();
  const role = params.get('role') ?? 'Contributor';
  let user: string;
  if (account) {
    // AuthGate has already stored the real access token; nothing to do here but name the member.
    user = account.name ?? account.username;
  } else {
    user = params.get('user') ?? (import.meta.env.DEV ? devRandomUser() : 'Guest');
    // Dev/stub only: the identity IS the token, so this is where it gets established.
    setToken(`${user}|${role}`);
  }
  // The CRDT WebSocket. In dev it is same-origin at /ws (the Vite proxy forwards it straight
  // to the backend Hocuspocus server). In prod the SPA lives on a separate origin, so the WS
  // URL is resolved at runtime from window.__NIOBE_CONFIG__ (public/config.js) or a build-time
  // env var.
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const wsUrl =
    window.__NIOBE_CONFIG__?.backendWs ??
    import.meta.env.VITE_BACKEND_WS ??
    `${proto}://${window.location.host}/ws`;
  return {
    docName: params.get('doc'),
    wsUrl,
    user: { name: user, color: colorFor(user), role },
  };
}
